import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  CAPABILITY,
  PERMISSION_RULE,
} from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { USE_CASE } from '@trustero/trustero-api-web/lib/account/account_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import metadata from '@trustero/trustero-api-web/lib/authz/rpcAuthorization.json'
import md5 from 'md5'
import type { AuthRecord } from '../../context/authContext'

export type RpcAccess = 'allowed' | 'pending' | 'denied'
type RpcRule = {
  mode: string
  required: number[]
  anyOf: number[]
  stream: boolean
  buyer: boolean
  anonymous?: boolean
}
// Regenerate after backend RPC annotations or authentication routes change:
// python3 scripts/generate_frontend_rpc_authorization.py
export const rpcAuthorization: Record<string, RpcRule> = metadata

// These rules admit a collection query. Resource checks still belong to the
// server and checkCanPerformAction; evaluating an owner/label rule without a
// resource here would prevent the user from discovering their permitted rows.
export const canReadCapability = (
  auth: AuthRecord | null | undefined,
  capability: CAPABILITY,
): boolean => {
  const rule = auth?.permissions?.capabilities[capability]?.rule
  switch (rule) {
    case PERMISSION_RULE.ALWAYS:
    case PERMISSION_RULE.IF_OWNER:
    case PERMISSION_RULE.IF_OWNER_OR_ASSIGNEE:
    case PERMISSION_RULE.CONTROLLED_BY_LABEL:
      return true
    case PERMISSION_RULE.IF_TRUSTERO_USER:
      return !!auth?.email?.toLowerCase().endsWith('@trustero.com')
    default:
      return false
  }
}

export const getRpcPath = (method: unknown): string | undefined => {
  if (typeof method === 'string') return method
  if (typeof method !== 'function') return undefined
  // grpc-web emits the fully qualified wire path as a literal in both promise
  // and streaming methods. This remains stable when function names are minified.
  const named = method as { getMockName?: () => string }
  return (
    named.getMockName?.().match(/\/[\w.]+\/[\w]+/)?.[0] ||
    String(method).match(/\/[\w.]+\/[\w]+/)?.[0]
  )
}

const modelCapabilities: Partial<Record<MODEL_TYPE, CAPABILITY>> = {
  [MODEL_TYPE.OVERVIEW]: CAPABILITY.CAN_VIEW_COMPANY_INFO,
  [MODEL_TYPE.BESTPRACTICE]: CAPABILITY.CAN_VIEW_ROADMAP,
  [MODEL_TYPE.AUDIT]: CAPABILITY.CAN_VIEW_AUDITS,
  [MODEL_TYPE.CONTROL]: CAPABILITY.CAN_VIEW_CONTROLS,
  [MODEL_TYPE.POLICY]: CAPABILITY.CAN_VIEW_POLICIES,
  [MODEL_TYPE.SERVICE]: CAPABILITY.CAN_VIEW_SERVICES,
  [MODEL_TYPE.COMPLIANCE_FRAMEWORK]: CAPABILITY.CAN_VIEW_FRAMEWORKS,
  [MODEL_TYPE.SCOPE]: CAPABILITY.CAN_VIEW_SCOPE,
}

const documentCapabilities: Partial<Record<DOCUMENT_TYPE, CAPABILITY>> = {
  [DOCUMENT_TYPE.POLICYDOC]: CAPABILITY.CAN_VIEW_POLICY_DOCS,
  [DOCUMENT_TYPE.EVIDENCE]: CAPABILITY.CAN_VIEW_EVIDENCE,
  [DOCUMENT_TYPE.COMPANY_INFO]: CAPABILITY.CAN_VIEW_COMPANY_INFO_DOCS,
}
const subjectDocumentCapabilities: Partial<Record<MODEL_TYPE, CAPABILITY>> = {
  [MODEL_TYPE.POLICY]: CAPABILITY.CAN_VIEW_POLICY_DOCS,
  [MODEL_TYPE.CONTROL]: CAPABILITY.CAN_VIEW_EVIDENCE,
  [MODEL_TYPE.OVERVIEW]: CAPABILITY.CAN_VIEW_COMPANY_INFO_DOCS,
}

export const getRpcAccess = (
  method: unknown,
  auth: AuthRecord | null | undefined,
  request?: unknown,
): RpcAccess => {
  const path = getRpcPath(method)
  const rule = path ? rpcAuthorization[path] : undefined
  if (!rule) return 'denied'
  // PUBLIC skips capability checks, not authentication. Only the server's
  // auth-exempt routes can run without a token (sign-in, public portals, etc.).
  if (!rule.anonymous && !auth?.accessToken)
    return auth?.isAuthLoading ? 'pending' : 'denied'
  // Buyer RPCs follow the server's separate route allowlist. Their role's page
  // permissions still govern page access. Public portal RPCs enforce their own session.
  if (rule.mode === 'PUBLIC') return 'allowed'
  if (auth?.useCase === USE_CASE.SAAS_BUYER)
    return rule.buyer ? 'allowed' : 'denied'
  if (rule.mode !== 'PROTECTED') return 'denied'
  if (auth?.isAuthLoading) return 'pending'
  if (!rule.required.length && !rule.anyOf.length) return 'denied'
  if (!rule.required.every((cap) => canReadCapability(auth, cap)))
    return 'denied'
  if (
    rule.anyOf.length &&
    !rule.anyOf.some((cap) => canReadCapability(auth, cap))
  )
    return 'denied'
  if (
    request &&
    (path === '/model.Model/GetByModelType' ||
      path === '/model.Model/GetCountByModelType')
  ) {
    const modelType = (
      request as { getModeltype?: () => MODEL_TYPE }
    )?.getModeltype?.()
    const capability =
      modelType === undefined ? undefined : modelCapabilities[modelType]
    if (capability === undefined || !canReadCapability(auth, capability))
      return 'denied'
  }
  if (request && path === '/attachment.Attachment/GetDocumentCounts') {
    const docType = (
      request as { getDocumenttype?: () => DOCUMENT_TYPE }
    ).getDocumenttype?.()
    const cap =
      docType === undefined ? undefined : documentCapabilities[docType]
    if (cap === undefined || !canReadCapability(auth, cap)) return 'denied'
  }
  if (request && path === '/attachment.Attachment/GetDocumentsBySubjectIDs') {
    const subjects =
      (
        request as {
          getSubjectidsList?: () => Array<{ getModeltype(): MODEL_TYPE }>
        }
      ).getSubjectidsList?.() || []
    if (
      !subjects.length ||
      !subjects.every((subject) => {
        const cap = subjectDocumentCapabilities[subject.getModeltype()]
        return cap !== undefined && canReadCapability(auth, cap)
      })
    )
      return 'denied'
  }
  return 'allowed'
}

export class RpcAccessDenied extends Error {
  readonly code = 7
  readonly name = 'RpcAccessDenied'
  constructor(method?: unknown) {
    super(
      `Access restricted${getRpcPath(method) ? `: ${getRpcPath(method)}` : ''}`,
    )
  }
}

export const isPermissionDenied = (error: unknown): boolean =>
  (error as { code?: number })?.code === 7 &&
  !/Invalid (token|permissions) version/.test((error as Error)?.message || '')

// Include scope and preview changes even when the server permission version has
// not changed. Cache consumers and invalidators use the same identity.
const authorizationScopes = new WeakMap<AuthRecord, string>()
export const getAuthorizationCacheScope = (
  auth?: AuthRecord | null,
): string => {
  if (!auth) return 'anonymous'
  const cached = authorizationScopes.get(auth)
  if (cached) return cached
  const scope = md5(
    JSON.stringify([
      auth.accountId,
      auth.email,
      auth.useCase,
      auth.isAuthenticated,
      auth.isAuthLoading,
      auth.permissions,
      auth.impersonation,
    ]),
  )
  authorizationScopes.set(auth, scope)
  return scope
}
